import { CKEditor } from '@ckeditor/ckeditor5-react'
import {
    ClassicEditor,
    Essentials,
    EventInfo,
    Paragraph,
    Undo,
} from 'ckeditor5'
import {
    Comments,
    SuggestionData,
    TrackChanges,
    TrackChangesData,
} from 'ckeditor5-premium-features'
import 'ckeditor5-premium-features/ckeditor5-premium-features.css'
import 'ckeditor5/ckeditor5.css'
import { useChatProfileStore } from 'components/AskDelaChat/ChatProfileStore'
import { processEnv } from 'components/configuration/environmentVariables'
import { useHtmlParagraphPaste } from 'components/WorkspaceEditor/features/copypaste/hooks/useHtmlParagraphPaste'
import { useHighlightStoreShallowSelector } from 'components/WorkspaceEditor/HighlightStore'
import React, { memo, useCallback, useMemo, useRef } from 'react'
import { ContentEditorLayout } from './ContentEditorLayout'
import {
    hideBalloonPanelsOnMutation,
    hideEvaluationBadge,
    HIGHLIGHT_CLASS,
} from './contentUtils'
import { TextIdPlugin, TrackChangePlugin, UserPlugin } from './model'

/**
 * A type of callback function to be called when the editor is ready
 * @param editor - The editor instance
 */
export type ClassicEditorOnReadyHandler = (editor: ClassicEditor) => void

/**
 * A type of callback function to be called when the editor loses focus
 */
export type ClassicEditorHandler = (
    /** The event information */
    event: EventInfo,
    /** The editor instance */
    editor: ClassicEditor
) => void

/** Props for the ContentEditor component */
export interface ContentEditorProps {
    /** Initial editor data in HTML format */
    initialData?: string
    /** Initial track changes (suggestions) to load into the editor (this is also used for collaborators configuration) */
    initialSuggestions?: SuggestionData[]
    handlers?: {
        /** Callback function to be called when the editor is ready */
        onReady?: ClassicEditorOnReadyHandler
        /** Callback function to be called when the editor loses focus */
        onBlur?: ClassicEditorHandler
        /** Callback function to be called when the editor changes */
        onChange?: ClassicEditorHandler
    }
}

// editor plugins
const EDITOR_PLUGINS = [
    Essentials,
    Paragraph,
    Undo,
    UserPlugin,
    Comments,
    TrackChanges,
    TrackChangePlugin,
    TrackChangesData,
    TextIdPlugin,
]

// Comment Configs
const COMMENTS_CONFIG = {
    editorConfig: {
        extraPlugins: [Comments],
        sidebar: {
            container: document.body,
        },
    },
}

/**
 * A memoized ContentEditor component.
 * Create an instance of the ContentEditor component with overridden layout.
 * @param {ContentEditorProps} props - The props for the ContentEditor component.
 * @returns {React.ReactElement} Rendered ContentEditor component
 */
export const ContentEditor = memo<ContentEditorProps>(
    ({ initialData = '<p>:smiley:</p>', initialSuggestions, handlers }) => {
        const { onReady, onBlur, onChange } = handlers || {}
        const requestHighlight = useHighlightStoreShallowSelector(
            (s) => s.requestHighlight
        )
        const { handlePaste } = useHtmlParagraphPaste()
        const hasInitiallyHighlighted = useRef(false)
        const isFirefox = useRef(
            navigator.userAgent.toLowerCase().includes('firefox')
        )
        const chatUser = useChatProfileStore((state) => state.chatUser)

        const editorConfig = useMemo(
            () => ({
                licenseKey: processEnv.REACT_APP_CKEditorLicenseKey,
                suggestions: initialSuggestions,
                plugins: EDITOR_PLUGINS,
                me: {
                    id: chatUser?.id ?? '',
                    name: `${chatUser?.profileInfo?.firstName ?? ''}${
                        chatUser?.profileInfo?.lastName ?? ''
                    }`.toUpperCase(),
                },
                comments: COMMENTS_CONFIG,
            }),
            [
                initialSuggestions,
                chatUser?.id,
                chatUser?.profileInfo?.firstName,
                chatUser?.profileInfo?.lastName,
            ]
        )

        /**
         * Handler for when the editor is ready.
         * Configures the editor, sets up event listeners, and handles initial highlighting.
         * @param {ClassicEditor} editor - The CKEditor instance
         */
        const onReadyHandler = useCallback(
            async (editor: ClassicEditor) => {
                onReady?.(editor)

                // Configure editor
                // disable the Enter key using the key name 'Enter'
                editor?.keystrokes?.set('Enter', (_, cancel) => cancel())
                editor?.execute('trackChanges')

                // Prevent user from adding text before, after, above, or below block-level widgets like tables.
                // (Our tables are treated as a standalone unit, like our paragraphs)
                editor?.plugins
                    .get('WidgetTypeAround')
                    ?.forceDisabled('OurApplication')

                // Inspector for debugging
                if (processEnv?.REACT_APP_CKEditorInspector === '1') {
                    const { default: CKEditorInspector } = await import(
                        '@ckeditor/ckeditor5-inspector'
                    )
                    CKEditorInspector.attach(editor)
                }

                // Register the paste handler for HTML paragraphs
                editor.editing.view.document.on('paste', handlePaste)

                // Focus handling with Firefox fix
                editor.editing.view.document.on('focus', () => {
                    hideBalloonPanelsOnMutation()
                    hideEvaluationBadge()

                    const firstSuggestionId = initialSuggestions?.[0]?.id
                    if (!isFirefox.current && firstSuggestionId) {
                        requestHighlight(firstSuggestionId, HIGHLIGHT_CLASS)
                    }
                })

                // Firefox-specific: highlight on ready instead of focus
                if (
                    isFirefox.current &&
                    !hasInitiallyHighlighted.current &&
                    initialSuggestions?.[0]?.id
                ) {
                    hasInitiallyHighlighted.current = true
                    setTimeout(() => {
                        requestHighlight(
                            initialSuggestions[0].id,
                            HIGHLIGHT_CLASS
                        )
                    }, 100)
                }
            },
            [onReady, initialSuggestions, requestHighlight, handlePaste]
        )

        return (
            <ContentEditorLayout>
                <CKEditor
                    data-testid="content-editor"
                    editor={ClassicEditor}
                    config={editorConfig}
                    data={initialData}
                    onReady={onReadyHandler}
                    onBlur={onBlur}
                    onChange={onChange}
                />
            </ContentEditorLayout>
        )
    }
)
